import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, FileText, Filter, Loader2, ScrollText, X } from "lucide-react";
import { api } from "../../lib/api";
import { ScrollPanel } from "../ui/scroll-panel";

/**
 * QENVA Executive Report Launcher.
 *
 * Modal panel exposed from the Dashboard "Executive Report Gateway".
 * Lets the user pick:
 *   1. An audience template (CEO / Bank / Investor)
 *   2. A scope (workspace OR manual document selection)
 *   3. A confidentiality marking
 * Then POSTs /api/exec-report and triggers a browser download.
 *
 * Pure presentational + a single download call. No mutations.
 */
const CONFIDENTIALITY_LEVELS = [
  { value: "Public", label_fr: "Publique" },
  { value: "Internal", label_fr: "Interne" },
  { value: "Confidential", label_fr: "Confidentiel" },
  { value: "Strictly Confidential", label_fr: "Strictement Confidentiel" },
];

const TEMPLATE_ICON = {
  ceo: ScrollText,
  bank: FileText,
  investor: Filter,
};

const TEMPLATE_BADGE = {
  ceo: { ring: "ring-qenva-green/30", icon: "text-qenva-green", bg: "bg-qenva-green/10" },
  bank: { ring: "ring-qenva-blue/30", icon: "text-qenva-blue", bg: "bg-qenva-blue/10" },
  investor: { ring: "ring-[#8B5CF6]/30", icon: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10" },
};

export default function ExecutiveReportLauncher({ open, onClose }) {
  const { t, i18n } = useTranslation("dashboard");
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ confidentiality_levels: [] });
  const [docs, setDocs] = useState([]);

  const [template, setTemplate] = useState("ceo");
  const [scope, setScope] = useState("workspace");
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [confidentiality, setConfidentiality] = useState("Confidential");
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load templates and docs when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/exec-report/templates").catch(() => ({ data: { templates: [] } })),
      api.get("/documents", { params: { limit: 200 } }).catch(() => ({ data: [] })),
    ])
      .then(([tplRes, docsRes]) => {
        if (cancelled) return;
        setTemplates(tplRes.data?.templates || []);
        setMeta({
          confidentiality_levels: tplRes.data?.confidentiality_levels || [],
          scopes: tplRes.data?.scopes || [],
        });
        const list = Array.isArray(docsRes.data) ? docsRes.data : (docsRes.data?.items || []);
        setDocs(list);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const eligibleDocs = useMemo(
    () => docs.filter((d) => !d.is_deleted),
    [docs]
  );

  function toggleDoc(id) {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedDocs(new Set(eligibleDocs.map((d) => d.id)));
  }

  function clearAll() {
    setSelectedDocs(new Set());
  }

  async function handleGenerate() {
    if (scope === "documents" && selectedDocs.size === 0) {
      toast.error(t("v3.exec_report.errors.no_docs"));
      return;
    }
    setGenerating(true);
    try {
      const payload = {
        template,
        scope,
        confidentiality,
        ...(scope === "documents" ? { document_ids: Array.from(selectedDocs) } : {}),
      };
      const resp = await api.post("/exec-report", payload, { responseType: "blob" });
      // Extract filename from Content-Disposition
      const cd = resp.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `QENVA-${template}.pdf`;
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(t("v3.exec_report.success_toast", { template: template.toUpperCase() }));
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "—";
      toast.error(`${t("v3.exec_report.errors.generic")} : ${typeof detail === "string" ? detail : "Erreur serveur"}`);
    } finally {
      setGenerating(false);
    }
  }

  if (!open) return null;

  const node = (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-zinc-950/55 backdrop-blur-sm animate-fade-up"
      onClick={onClose}
      data-testid="exec-report-launcher-overlay"
    >
      <div
        className="bg-white w-full sm:w-[640px] max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="exec-report-launcher-modal"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="qenva-kicker !text-qenva-green">{t("v3.exec_report.kicker")}</p>
            <h3 className="font-display font-bold text-qenva-navy text-[19px] mt-1 tracking-tight">
              {t("v3.exec_report.title")}
            </h3>
            <p className="text-[12.5px] text-zinc-500 mt-1 leading-snug">
              {t("v3.exec_report.subtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="exec-report-launcher-close"
            className="p-1.5 rounded-lg hover:bg-zinc-100 transition-colors text-zinc-500"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <ScrollPanel className="px-6 py-5 space-y-5">
          {/* Step 1 — Audience */}
          <section data-testid="exec-report-step-template">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-2">
              {t("v3.exec_report.step_template")}
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {templates.length === 0 && loading && (
                <div className="col-span-3 text-zinc-400 text-sm">{t("v3.exec_report.loading_templates")}</div>
              )}
              {templates.map((tpl) => {
                const Icon = TEMPLATE_ICON[tpl.key] || FileText;
                const palette = TEMPLATE_BADGE[tpl.key] || TEMPLATE_BADGE.ceo;
                const active = template === tpl.key;
                return (
                  <button
                    key={tpl.key}
                    type="button"
                    onClick={() => setTemplate(tpl.key)}
                    data-testid={`exec-report-template-${tpl.key}`}
                    className={`text-left p-3.5 rounded-xl border transition-all min-w-0 ${
                      active
                        ? "border-qenva-navy/80 ring-2 ring-qenva-navy/20 bg-zinc-50"
                        : "border-zinc-200 hover:border-zinc-300 bg-white"
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg grid place-items-center mb-2 ${palette.bg} ${palette.icon} ring-1 ${palette.ring}`}>
                      <Icon size={17} strokeWidth={1.75} />
                    </div>
                    <p className="font-display font-bold text-qenva-navy text-[13.5px] truncate">{tpl.audience_label}</p>
                    <p className="text-[11.5px] text-zinc-500 mt-0.5 leading-tight line-clamp-3">{tpl.subtitle}</p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 2 — Scope */}
          <section data-testid="exec-report-step-scope">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-2">
              {t("v3.exec_report.step_scope")}
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {[
                { value: "workspace", label: t("v3.exec_report.scope.workspace"), sub: t("v3.exec_report.scope.workspace_hint") },
                { value: "documents", label: t("v3.exec_report.scope.documents"), sub: t("v3.exec_report.scope.documents_hint") },
              ].map((opt) => {
                const active = scope === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setScope(opt.value)}
                    data-testid={`exec-report-scope-${opt.value}`}
                    className={`text-left px-3.5 py-2.5 rounded-xl border ${
                      active
                        ? "border-qenva-navy/80 ring-2 ring-qenva-navy/20 bg-zinc-50"
                        : "border-zinc-200 hover:border-zinc-300 bg-white"
                    }`}
                  >
                    <p className="font-semibold text-qenva-navy text-[13px]">{opt.label}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{opt.sub}</p>
                  </button>
                );
              })}
            </div>
            {scope === "documents" && (
              <div className="border border-zinc-200 rounded-xl bg-zinc-50/60">
                <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-bold">
                    {t("v3.exec_report.docs.selected_count", { count: selectedDocs.size, total: eligibleDocs.length })}
                  </p>
                  <div className="flex gap-1">
                    <button onClick={selectAll} type="button" data-testid="exec-report-select-all" className="text-[11px] text-qenva-blue hover:underline">
                      {t("v3.exec_report.docs.select_all")}
                    </button>
                    <span className="text-zinc-300">·</span>
                    <button onClick={clearAll} type="button" data-testid="exec-report-clear-all" className="text-[11px] text-zinc-500 hover:underline">
                      {t("v3.exec_report.docs.clear_all")}
                    </button>
                  </div>
                </div>
                <div className="max-h-44 overflow-y-auto divide-y divide-zinc-100">
                  {eligibleDocs.length === 0 && (
                    <p className="text-[12px] text-zinc-400 px-3 py-3">{t("v3.exec_report.docs.empty")}</p>
                  )}
                  {eligibleDocs.map((d) => {
                    const checked = selectedDocs.has(d.id);
                    return (
                      <label
                        key={d.id}
                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-white cursor-pointer"
                        data-testid={`exec-report-doc-${d.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDoc(d.id)}
                          className="rounded border-zinc-300"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[12.5px] text-zinc-900 truncate">{d.filename}</span>
                          <span className="block text-[10.5px] text-zinc-500 truncate">
                            {(d.document_type || "—")} · {(d.status || "—")}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Step 3 — Confidentiality */}
          <section data-testid="exec-report-step-confidentiality">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-2">
              {t("v3.exec_report.step_confidentiality")}
            </p>
            <div className="flex flex-wrap gap-2">
              {CONFIDENTIALITY_LEVELS.map((c) => {
                const active = confidentiality === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setConfidentiality(c.value)}
                    data-testid={`exec-report-conf-${c.value.replace(/\s+/g, '_').toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full border text-[12px] font-semibold transition-all ${
                      active
                        ? "border-qenva-navy bg-qenva-navy text-white"
                        : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                    }`}
                  >
                    {i18n.language === "fr" ? c.label_fr : c.value}
                  </button>
                );
              })}
            </div>
          </section>
        </ScrollPanel>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50/70 shrink-0">
          <button
            onClick={onClose}
            type="button"
            data-testid="exec-report-cancel"
            className="px-3.5 py-2 rounded-lg text-[13px] text-zinc-700 hover:bg-zinc-100"
          >
            {t("v3.exec_report.cancel")}
          </button>
          <button
            onClick={handleGenerate}
            type="button"
            disabled={generating || loading}
            data-testid="exec-report-generate"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold bg-qenva-navy text-white hover:bg-qenva-navy/90 disabled:opacity-60 disabled:cursor-wait"
          >
            {generating ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            <span>{generating ? t("v3.exec_report.generating") : t("v3.exec_report.generate_cta")}</span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
